/**
 * Operations home — portfolio snapshot and role-based shortcuts.
 */
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { PageState } from "../../../shared/components/PageChrome";
import { AppAlert } from "../../../shared/components/ui";
import { api } from "../../../shared/api/client";
import { getUser, userDisplayName, userDisplayRole } from "../../auth/session";

type DeviceStats = {
  by_status: Record<string, number>;
  total: number;
};

type MethodStat = { count: number; total: number };

type PaymentStats = {
  total_count: number;
  total_revenue: number;
  by_method: Record<string, MethodStat>;
};

type Stats = {
  devices: DeviceStats;
  payments: PaymentStats;
  total_customers: number;
};

type RoleGuide = {
  role: string;
  label: string;
  startTo: string;
  startLabel: string;
  focus: string;
};

const ROLE_GUIDES: RoleGuide[] = [
  {
    role: "system_admin",
    label: "System admin",
    startTo: "/admin/health",
    startLabel: "System Health",
    focus: "Check service health, then manage users and tenants.",
  },
  {
    role: "business_admin",
    label: "Business admin",
    startTo: "/bulk-import",
    startLabel: "Bulk Import",
    focus: "Import customers or devices, then review payments and reminders.",
  },
  {
    role: "commercial_officer",
    label: "Commercial officer",
    startTo: "/payment-approvals",
    startLabel: "Payment Approvals",
    focus: "Approve pending payments, then clear KYC and key approval queues.",
  },
  {
    role: "sales_rep",
    label: "Sales representative",
    startTo: "/field/leads",
    startLabel: "Leads / KYC",
    focus: "Capture leads in the field, then record payments when ready.",
  },
  {
    role: "technician",
    label: "Technician",
    startTo: "/field/installations",
    startLabel: "My Installations",
    focus: "Continue assigned installations and submit evidence for approval.",
  },
  {
    role: "village_chairman",
    label: "Village chairman",
    startTo: "/vc",
    startLabel: "My Village",
    focus: "Verify community members and review customers in your areas.",
  },
  {
    role: "m_and_e_officer",
    label: "M&E / REA officer",
    startTo: "/analytics",
    startLabel: "Analytics",
    focus: "Review portfolio health and dMRV progress for your tenant.",
  },
  {
    role: "customer",
    label: "Customer",
    startTo: "/portal",
    startLabel: "My Account",
    focus: "View your contract, payments, and installation status.",
  },
];

/** Extra shortcuts shown beside the primary CTA — filtered by role. */
const QUICK_LINKS: { to: string; label: string; roles?: string[] }[] = [
  { to: "/customers", label: "Customers", roles: ["system_admin", "business_admin", "commercial_officer", "sales_rep", "m_and_e_officer", "rea_officer", "village_chairman"] },
  { to: "/payment-approvals", label: "Payment Approvals", roles: ["system_admin", "business_admin", "commercial_officer"] },
  { to: "/kyc-review", label: "KYC Review", roles: ["system_admin", "business_admin", "commercial_officer"] },
  { to: "/field/payments/new", label: "Record Payment", roles: ["system_admin", "business_admin", "commercial_officer", "sales_rep"] },
  { to: "/sms-center", label: "SMS Center", roles: ["system_admin", "business_admin", "commercial_officer"] },
  { to: "/map", label: "Customer Map", roles: ["system_admin", "business_admin", "commercial_officer", "technician", "sales_rep", "m_and_e_officer", "village_chairman"] },
  { to: "/analytics", label: "Analytics", roles: ["system_admin", "business_admin", "commercial_officer", "m_and_e_officer", "rea_officer"] },
];

function StatCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: "success" | "warning" | "error" | "info" | "default";
}) {
  const color =
    tone === "success"
      ? "success.main"
      : tone === "warning"
        ? "warning.main"
        : tone === "error"
          ? "error.main"
          : tone === "info"
            ? "info.main"
            : "divider";
  return (
    <Card variant="outlined" sx={{ height: "100%", borderTop: 3, borderColor: color }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.04 }}>
          {label.toUpperCase()}
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700, fontSize: "1.75rem" }}>
          {value}
        </Typography>
        {sub ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {sub}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function OperationsDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [pageState, setPageState] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const user = getUser();

  const load = async () => {
    setPageState("loading");
    setPageError("");
    const [devicesResult, paymentsResult, customersResult] = await Promise.allSettled([
      api.get<DeviceStats>("/devices/stats"),
      api.get<PaymentStats>("/payments/stats"),
      api.get<{ total: number }>("/customers?per_page=1&page=1"),
    ]);
    const failures = [
      devicesResult.status === "rejected" ? "device statistics" : null,
      paymentsResult.status === "rejected" ? "payment statistics" : null,
      customersResult.status === "rejected" ? "customer count" : null,
    ].filter(Boolean);
    setStats({
      devices: devicesResult.status === "fulfilled"
        ? devicesResult.value
        : { by_status: {}, total: 0 },
      payments: paymentsResult.status === "fulfilled"
        ? paymentsResult.value
        : { total_count: 0, total_revenue: 0, by_method: {} },
      total_customers: customersResult.status === "fulfilled"
        ? customersResult.value.total ?? 0
        : 0,
    });
    if (failures.length === 3) {
      setPageError("Dashboard statistics are unavailable for this account.");
      setPageState("error");
    } else {
      if (failures.length > 0) {
        setPageError(`Some data is unavailable: ${failures.join(", ")}.`);
      }
      setPageState("success");
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const myGuide = useMemo(() => {
    const role = user?.role;
    if (!role) return null;
    if (role === "rea_officer") return ROLE_GUIDES.find((g) => g.role === "m_and_e_officer") ?? null;
    return ROLE_GUIDES.find((g) => g.role === role) ?? null;
  }, [user?.role]);

  if (pageState === "loading" || pageState === "error") {
    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="h2">Operations</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Portfolio snapshot and role shortcuts.
          </Typography>
        </Box>
        <PageState status={pageState} error={pageError} onRetry={() => void load()} />
      </Stack>
    );
  }

  const d = stats!;
  const activeDevices = d.devices.by_status?.active ?? 0;
  const suspendedDevices = d.devices.by_status?.suspended ?? 0;
  const displayName = user ? userDisplayName(user) : "";
  const displayRole = user ? userDisplayRole(user) : "";

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}
      >        <Box>
          <Typography variant="h2">Operations</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Welcome back{displayName ? `, ${displayName}` : ""}
            {displayRole ? ` · ${displayRole}` : ""}.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load()}>
          Refresh
        </Button>
      </Stack>

      {pageError ? <AppAlert severity="warning">{pageError}</AppAlert> : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Total customers" value={d.total_customers.toLocaleString()} sub="registered on platform" tone="info" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Active devices" value={activeDevices.toLocaleString()} sub={`${d.devices.total ?? 0} total fleet`} tone="success" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Suspended devices"
            value={suspendedDevices.toLocaleString()}
            sub="awaiting payment"
            tone={suspendedDevices > 0 ? "warning" : "default"}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Revenue (completed)"
            value={`TZS ${(d.payments.total_revenue ?? 0).toLocaleString()}`}
            sub={`${d.payments.total_count ?? 0} payments`}
            tone="success"
          />
        </Grid>
      </Grid>

      {myGuide ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h3" gutterBottom>
              Suggested next step
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {myGuide.focus}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
              <Button component={RouterLink} to={myGuide.startTo} variant="contained">
                Open {myGuide.startLabel}
              </Button>
              {QUICK_LINKS.filter(
                (l) =>
                  l.to !== myGuide.startTo &&
                  (!l.roles || (user?.role != null && l.roles.includes(user.role))),
              )
                .slice(0, 4)
                .map((l) => (
                  <Button key={l.to} component={RouterLink} to={l.to} variant="outlined">
                    {l.label}
                  </Button>
                ))}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Device fleet
              </Typography>
              <Stack spacing={1}>
                {Object.entries(d.devices.by_status ?? {}).map(([status, count]) => (
                  <Stack
                    key={status}
                    direction="row"
                    sx={{ justifyContent: "space-between", alignItems: "center" }}
                  >
                    <Chip
                      size="small"
                      label={status.replace(/_/g, " ")}
                      color={
                        status === "active"
                          ? "success"
                          : status === "suspended"
                            ? "error"
                            : status === "unassigned"
                              ? "default"
                              : "info"
                      }
                      variant="outlined"
                    />
                    <Typography sx={{ fontWeight: 700 }}>{count}</Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Payments by method
              </Typography>
              {d.payments.by_method && Object.keys(d.payments.by_method).length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Method</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Total (TZS)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(d.payments.by_method)
                        .filter(([, stat]) => (stat?.count ?? 0) > 0)
                        .map(([method, stat]) => (
                          <TableRow key={method}>
                            <TableCell>
                              {method.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                            </TableCell>
                            <TableCell align="right">{stat?.count ?? 0}</TableCell>
                            <TableCell align="right">{(stat?.total ?? 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No completed payments yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
